import { Notification } from "@/components/admin/notification";
import { PasswordInput } from "@/components/admin/password-input";
import { TextInput } from "@/components/admin/text-input";
import { useConfigurationContext } from "@/components/gsw-erp/root/ConfigurationContext.tsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Form, required, useLogin, useNotify } from "ra-core";
import { useEffect, useRef, useState } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { SSOAuthButton } from "./SSOAuthButton";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const {
    title,
    googleWorkplaceDomain,
    disableEmailPasswordAuthentication,
  } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const [isInitializing, setIsInitializing] = useState(true);

  // Simulated initial load for the Skeleton effect
  useEffect(() => {
    const timer = setTimeout(() => setIsInitializing(false), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify(
      "If you're a registered user, you should receive a password recovery email shortly.",
      {
        type: "success",
      },
    );

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="relative grid w-full lg:grid-cols-2">
        <div className="relative hidden h-full flex-col overflow-hidden bg-muted/40 p-10 lg:flex">
          {/* Ambient brand-color glows, no raw hex — driven by the primary token */}
          <div className="pointer-events-none absolute -top-1/4 -left-1/4 size-[600px] rounded-full bg-primary/10 blur-[100px]" />
          <div className="pointer-events-none absolute -right-1/4 -bottom-1/4 size-[500px] rounded-full bg-primary/5 blur-[100px]" />

          <div className="pointer-events-none absolute top-4 left-1/2 z-10 flex w-full -translate-x-1/2 justify-center">
            <img
              className="h-20 object-contain drop-shadow-2xl select-none md:h-28 lg:h-36 xl:h-44 dark:brightness-0 dark:invert"
              src="/logos/rovasys_horizontal.png"
              alt="Rovasys Logo"
              draggable={false}
            />
          </div>

          <div className="relative z-10 mt-28 flex items-center justify-between lg:mt-32">
            <div className="invisible flex items-center text-xl font-bold text-foreground">
              {title}
            </div>
          </div>
          <div className="relative z-10 mt-2 flex w-full flex-1 flex-col items-center justify-center">
            <img
              src="/login-illustration.png"
              alt="Team Collaboration"
              className="w-[85%] max-w-lg object-contain drop-shadow-2xl select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="mt-6 max-w-md space-y-3 text-center">
              <h2 className="text-3xl font-extrabold tracking-tight text-foreground">
                Streamline Your Operations
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground">
                Connect your team, automate workflows, and gain deep insights into your business with GSW ERP.
              </p>
            </div>
          </div>
        </div>

        <div className="relative flex w-full flex-col justify-center overflow-hidden bg-background p-4 lg:p-12">
          <div className="pointer-events-none absolute top-0 right-0 size-[800px] -translate-y-1/2 translate-x-1/2 rounded-full bg-primary/5 blur-[100px]" />
          <div className="pointer-events-none absolute bottom-0 left-0 size-[600px] -translate-x-1/4 translate-y-1/2 rounded-full bg-primary/5 blur-[100px]" />

          <Card className="relative z-10 w-full space-y-8 rounded-3xl border-border/60 bg-card/80 p-8 shadow-xl backdrop-blur-xl sm:p-10 lg:mx-auto lg:w-[420px]">
            {isInitializing ? (
              <div className="animate-in fade-in space-y-8 duration-500">
                <div className="flex flex-col items-center space-y-3">
                  <Skeleton className="h-10 w-48 rounded-lg" />
                  <Skeleton className="h-4 w-64 rounded-md" />
                </div>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 rounded-md" />
                    <Skeleton className="h-11 w-full rounded-lg" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 rounded-md" />
                    <Skeleton className="h-11 w-full rounded-lg" />
                  </div>
                  <div className="flex justify-between pt-2">
                    <Skeleton className="h-4 w-28 rounded-md" />
                    <Skeleton className="h-4 w-32 rounded-md" />
                  </div>
                  <Skeleton className="mt-4 h-11 w-full rounded-lg" />
                </div>
              </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="mb-8 space-y-2 text-center">
                  <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
                    Welcome back
                  </h1>
                  <p className="text-sm font-medium text-muted-foreground">
                    Please enter your details to sign in.
                  </p>
                </div>
                {disableEmailPasswordAuthentication ? null : (
                  <Form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-5">
                      <TextInput
                        label="Email Address"
                        source="email"
                        type="email"
                        validate={required()}
                      />
                      <PasswordInput
                        label="Password"
                        source="password"
                        validate={required()}
                      />
                    </div>

                    <div className="flex items-center justify-between pt-1 pb-2">
                      <div className="flex items-center gap-2.5">
                        <Checkbox id="remember-me" name="remember-me" />
                        <Label htmlFor="remember-me" className="cursor-pointer text-sm font-medium">
                          Remember me
                        </Label>
                      </div>
                      {disableEmailPasswordAuthentication ? null : (
                        <Link
                          to={"/forgot-password"}
                          className="text-sm font-semibold text-primary transition-colors hover:text-primary/80"
                        >
                          Forgot password?
                        </Link>
                      )}
                    </div>

                    <Button type="submit" className="w-full" size="lg" disabled={loading}>
                      {loading ? (
                        <Spinner className="size-4 text-primary-foreground" />
                      ) : (
                        "Sign in to Dashboard"
                      )}
                    </Button>
                  </Form>
                )}
                {googleWorkplaceDomain ? (
                  <div className="mt-8">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="bg-card px-3 font-medium text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>
                    <div className="mt-6">
                      <SSOAuthButton
                        className="w-full"
                        variant="outline"
                        size="lg"
                        domain={googleWorkplaceDomain}
                      >
                        Google Workplace
                      </SSOAuthButton>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </Card>
        </div>
      </div>
      <Notification />
    </div>
  );
};
